/**
 * Page Mon Compte (US-020)
 * E-Store - FrontOffice
 *
 * Fonctionnalités:
 * - Informations du profil utilisateur
 * - Historique des commandes
 * - Détails des commandes
 * - Annulation de commande
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useCart, orderApi, Order, ORDER_STATUS_LABELS, SearchableSelect, getBrandInfo } from '@e-store/shared';
import { CartBadge } from '../components/CartBadge';
import './MyAccountPage.css';

type Tab = 'profile' | 'orders';

export function MyAccountPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { cart } = useCart();

  // State
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [statusFilter, setStatusFilter] = useState('');

  // Modal
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Load orders when tab changes
  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab, page, statusFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await orderApi.getMyOrders({
        status: statusFilter || undefined,
        page,
        pageSize,
      });

      if (response.error) {
        setError(response.error);
        return;
      }

      setOrders(response.data?.orders || []);
      setTotal(response.data?.total || 0);
    } catch (err: any) {
      setError('Erreur lors du chargement des commandes');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderDetail = async (order: Order) => {
    try {
      setLoading(true);
      const response = await orderApi.getOrder(order.id);

      if (response.error) {
        setError(response.error);
        return;
      }

      setSelectedOrder(response.data?.order || null);
      setShowOrderDetail(true);
    } catch (err: any) {
      setError('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder || !cancelReason.trim()) {
      setError('Veuillez indiquer une raison d\'annulation');
      return;
    }

    try {
      setIsCancelling(true);
      const response = await orderApi.cancelOrder(selectedOrder.id, cancelReason.trim());

      if (response.error) {
        setError(response.error);
        return;
      }

      setSuccess('Commande annulée avec succès');
      setShowCancelModal(false);
      setShowOrderDetail(false);
      setCancelReason('');
      setSelectedOrder(null);
      loadOrders();
    } catch (err: any) {
      setError('Erreur lors de l\'annulation de la commande');
    } finally {
      setIsCancelling(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-MG', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price) + ' Ar';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusClass = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'confirmed':
        return 'status-confirmed';
      case 'processing':
        return 'status-processing';
      case 'shipped':
        return 'status-shipped';
      case 'delivered':
        return 'status-delivered';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const canCancelOrder = (order: Order) => {
    return order.status === 'pending' || order.status === 'confirmed';
  };

  const totalPages = Math.ceil(total / pageSize);

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="my-account-page">
      {/* Header */}
      <header className="fo-header">
        <div className="fo-header-content">
          <Link to="/" className="fo-logo">
            {getBrandInfo().name}
          </Link>

          <nav className="fo-nav">
            <Link to="/catalog">Catalogue</Link>
            <Link to="/my-account">Mon Compte</Link>
          </nav>

          <div className="fo-header-actions">
            <Link to="/cart" style={{ textDecoration: 'none', color: 'inherit' }}>
              <CartBadge count={cart?.itemCount || 0} />
            </Link>
            <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
              {user.firstName}
            </span>
            <button className="btn btn-secondary" onClick={logout}>
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Mon Compte</h1>
          <p className="page-description">Gérez votre profil et consultez vos commandes</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="message error">
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}
        {success && (
          <div className="message success">
            {success}
            <button onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Mon Profil
          </button>
          <button
            className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Mes Commandes
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="profile-card">
              <h2>Informations Personnelles</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Prénom</span>
                  <span className="info-value">{user.firstName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Nom</span>
                  <span className="info-value">{user.lastName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{user.email}</span>
                </div>
                {user.phone && (
                  <div className="info-item">
                    <span className="info-label">Téléphone</span>
                    <span className="info-value">{user.phone}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Rôle</span>
                  <span className="info-value">{user.role}</span>
                </div>
              </div>
            </div>

            <div className="profile-card">
              <h2>Paramètres du Compte</h2>
              <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                Pour modifier vos informations ou réinitialiser votre mot de passe, veuillez contacter notre service client.
              </p>
              <div className="profile-actions">
                <Link to="/catalog" className="btn btn-primary">
                  Continuer mes achats
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="orders-section">
            {/* Filters */}
            <div className="filters-bar">
              <SearchableSelect
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val);
                  setPage(1);
                }}
                options={[
                  { value: '', label: 'Tous les statuts' },
                  { value: 'pending', label: 'En attente' },
                  { value: 'confirmed', label: 'Confirmée' },
                  { value: 'processing', label: 'En préparation' },
                  { value: 'shipped', label: 'Expédiée' },
                  { value: 'delivered', label: 'Livrée' },
                  { value: 'cancelled', label: 'Annulée' },
                ]}
                placeholder="Tous les statuts"
                className="filter-select"
              />
            </div>

            {/* Orders List */}
            {loading ? (
              <div className="loading">
                <div className="loading-spinner" />
                <p>Chargement des commandes...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="empty-state">
                <p>Aucune commande trouvée</p>
                <Link to="/catalog" className="btn btn-primary">
                  Découvrir nos produits
                </Link>
              </div>
            ) : (
              <>
                <div className="orders-list">
                  {orders.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div>
                          <h3>Commande {order.orderNumber}</h3>
                          <p className="order-date">{formatDate(order.createdAt)}</p>
                        </div>
                        <span className={`order-status ${getStatusClass(order.status)}`}>
                          {ORDER_STATUS_LABELS[order.status]}
                        </span>
                      </div>

                      <div className="order-body">
                        <div className="order-info">
                          <p>
                            <strong>{order.items.length}</strong> article{order.items.length > 1 ? 's' : ''}
                          </p>
                          <p className="order-total">{formatPrice(order.totalTTC)}</p>
                        </div>

                        <div className="order-actions">
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleViewOrderDetail(order)}
                          >
                            Voir les détails
                          </button>
                          {canCancelOrder(order) && (
                            <button
                              className="btn btn-danger"
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowCancelModal(true);
                              }}
                            >
                              Annuler
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                    >
                      ← Précédent
                    </button>
                    <span className="page-info">
                      Page {page} sur {totalPages}
                    </span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page >= totalPages}
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowOrderDetail(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails de la Commande</h2>
              <button className="modal-close" onClick={() => setShowOrderDetail(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Informations</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Numéro</span>
                    <span className="detail-value">{selectedOrder.orderNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date</span>
                    <span className="detail-value">{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Statut</span>
                    <span className={`order-status ${getStatusClass(selectedOrder.status)}`}>
                      {ORDER_STATUS_LABELS[selectedOrder.status]}
                    </span>
                  </div>
                  {selectedOrder.shippingAddress && (
                    <div className="detail-item">
                      <span className="detail-label">Adresse de livraison</span>
                      <span className="detail-value">{selectedOrder.shippingAddress}</span>
                    </div>
                  )}
                  {selectedOrder.notes && (
                    <div className="detail-item">
                      <span className="detail-label">Notes</span>
                      <span className="detail-value">{selectedOrder.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Articles Commandés</h3>
                <div className="items-list">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="item-row">
                      <div className="item-details">
                        <p className="item-name">{item.articleName}</p>
                        <p className="item-reference">{item.articleReference}</p>
                      </div>
                      <div className="item-quantity">
                        <span>Qté: {item.quantity}</span>
                      </div>
                      <div className="item-price">
                        <span>{formatPrice(item.totalTTC)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h3>Récapitulatif</h3>
                <div className="summary-lines">
                  <div className="summary-line">
                    <span>Sous-total HT</span>
                    <span>{formatPrice(selectedOrder.subtotalHT)}</span>
                  </div>
                  <div className="summary-line">
                    <span>TVA</span>
                    <span>{formatPrice(selectedOrder.totalTVA)}</span>
                  </div>
                  <div className="summary-line summary-total">
                    <span>Total TTC</span>
                    <span>{formatPrice(selectedOrder.totalTTC)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {canCancelOrder(selectedOrder) && (
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setShowOrderDetail(false);
                    setShowCancelModal(true);
                  }}
                >
                  Annuler cette commande
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowOrderDetail(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Annuler la Commande</h2>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#64748b' }}>
                Commande: <strong>{selectedOrder.orderNumber}</strong>
              </p>
              <p style={{ marginBottom: '1rem', color: '#475569' }}>
                Êtes-vous sûr de vouloir annuler cette commande ?
              </p>

              <div className="form-group">
                <label htmlFor="cancelReason">Raison de l'annulation *</label>
                <textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Veuillez indiquer la raison de l'annulation..."
                  rows={4}
                  className="form-textarea"
                  required
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-danger"
                onClick={handleCancelOrder}
                disabled={isCancelling || !cancelReason.trim()}
              >
                {isCancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
